/**
 * LocationScreen (S3) — geofence via Telegram LocationManager (Bot API 8.0).
 * Props: { ctx, api, tg, nav, state, setState, params:{direction,branch_id?} }.
 *
 *   - On mount call tg.getLocation() → { lat, lng, accuracy }.
 *       throws 'DESKTOP'     → DESKTOP Rejections state (mobile-only).
 *       throws 'DENIED'      → ERROR_UI.DENIED, recover 'settings'.
 *       throws 'UNAVAILABLE' → ERROR_UI.UNAVAILABLE, recover 'retry'.
 *   - The radar just proves a fix was obtained; the real OUT_OF_RANGE verdict is
 *     server-side and surfaces in FACE (/checkin|/checkout).
 *   - MainButton "Davom etish" → setState({geo}) then push(LIVE,{direction,
 *     branch_id, geo}) — the in-app live-camera capture screen.
 */
import { useCallback, useEffect, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Button from '../components/Button.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { T } from '../lib/i18n.js';
import { SCREENS } from '../App.jsx';

export default function LocationScreen({ ctx, tg, nav, params, setState }) {
  const [phase, setPhase] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [geo, setGeo] = useState(null);
  const [errCode, setErrCode] = useState(null);

  const branchName =
    ctx.branches.find((b) => b.id === params?.branch_id)?.name ||
    ctx.branches[0]?.name ||
    '';

  const locate = useCallback(async () => {
    setPhase('loading');
    setErrCode(null);
    try {
      const g = await tg.getLocation();
      setGeo(g);
      setPhase('ok');
      tg.haptic('success');
    } catch (code) {
      // tg.getLocation throws a plain string: DESKTOP | DENIED | UNAVAILABLE
      setErrCode(typeof code === 'string' ? code : 'UNAVAILABLE');
      setPhase('error');
      tg.haptic('error');
    }
  }, [tg]);

  useEffect(() => {
    locate();
  }, [locate]);

  function proceed() {
    if (!geo) return;
    tg.haptic('selection');
    setState({ geo });
    nav.push(SCREENS.LIVE, {
      direction: params?.direction,
      branch_id: params?.branch_id,
      geo,
    });
  }

  // MainButton only lives on the 'ok' phase.
  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    if (phase !== 'ok') return undefined;
    return tg.MainButton.onClick(proceed);
  });

  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    if (phase !== 'ok') {
      tg.MainButton.hide();
      return undefined;
    }
    tg.MainButton.setText(T.btn_continue);
    tg.MainButton.show();
    return () => tg.MainButton.hide();
  }, [tg, phase]);

  if (phase === 'error') {
    return (
      <Screen title={T.title_location}>
        <ErrorState
          code={errCode}
          onRecover={(verb) => {
            if (verb === 'settings') {
              tg.openLocationSettings();
            } else if (verb === 'home') {
              nav.pop();
            } else {
              locate();
            }
          }}
        />
      </Screen>
    );
  }

  return (
    <Screen
      title={T.title_location}
      footer={
        !tg.isTelegram && phase === 'ok' ? (
          <Button variant="ok" onClick={proceed}>{T.btn_continue}</Button>
        ) : null
      }
    >
      <div className="radar">
        <div className="disc">
          <div className="core">📍</div>
        </div>
        {phase === 'loading' ? (
          <div className="loc-d">{T.loc_checking}</div>
        ) : (
          <>
            <div className="loc-h">
              {branchName ? `${branchName} ${T.loc_at_branch}` : T.title_location}
            </div>
            <div className="loc-d">{T.loc_confirmed}</div>
          </>
        )}
      </div>
    </Screen>
  );
}
